import { agentApi } from "@/api/AgentApi";
import { landlordApi } from "@/api/LandlordApi";
import { renterApi } from "@/api/RenterApi";
import { useUaePassLinkError } from "@/hooks/useUaePassLinkError";
import { unsubscribeFromPushNotifications } from "@/hooks/usePushNotificationsSetup";
import { authService, type LoginScreen } from "@/services/AuthService";
import { UserType } from "@/types/IdentityTypes";
import { identifyAnalyticsUser, recordSuccessfulLogin } from "@/lib/analytics";
import { useQueryClient } from "@tanstack/react-query";
import { User } from "oidc-client-ts";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { AuthContext } from "./UseAuth";

interface UserState {
  from?: string;
}

export interface AuthProviderProps {
  children: ReactNode;
  loadingElement?: ReactNode;
}

export const AuthProvider = ({
  children,
  loadingElement = null,
}: AuthProviderProps) => {
  const queryClient = useQueryClient();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const hasCheckedUaePassReturn = useRef(false);

  // Handle UAE Pass linking errors early, before routing decisions
  useUaePassLinkError();

  const refreshAuthState = useCallback(async () => {
    const authenticated = await authService.isAuthenticated();

    if (!authenticated) {
      console.log("User is not authenticated");
      setUser(null);
      setIsAuthenticated(false);
      unsubscribeFromPushNotifications({ localOnly: true });
      return;
    }

    console.log("User is authenticated");
    const user = await authService.getUser();
    setUser(user);
    setIsAuthenticated(true);
  }, []);

  // Handle UAE Pass return - refresh token, sync profile, and invalidate cache
  // This runs once on app initialization regardless of which page the user lands on
  useEffect(() => {
    if (hasCheckedUaePassReturn.current) return;
    hasCheckedUaePassReturn.current = true;

    const handleUaePassReturn = async () => {
      if (sessionStorage.getItem("uaepass-return") !== "true") return;

      sessionStorage.removeItem("uaepass-return");

      try {
        // 1. Refresh the authentication token to get updated claims (including isVerified)
        await authService.refreshUser();

        // 2. Get user type to determine which profile to sync
        const user = await authService.getUser();
        const userType = user?.profile?.UserType;
        const isVerified = user?.profile?.IsVerified === "True";

        if (isVerified) {
          // 3. Sync the profile in Core database with the updated identity claims
          // This updates FirstName, LastName, EmiratesId, Nationality, IsVerified, etc. from UAE Pass
          if (userType === UserType.Tenant) {
            await renterApi.syncProfileFromIdentity();
            await queryClient.invalidateQueries({
              queryKey: ["renter-profile"],
            });
          } else if (
            userType === UserType.Landlord ||
            userType === UserType.Manager
          ) {
            // Managers ride the landlord sync route — it upserts the User row
            // (required by the property-creation FK) and creates the thin
            // Landlord row CreatePropertyEndpoint would otherwise make lazily.
            await landlordApi.syncProfileFromIdentity();
            await queryClient.invalidateQueries({
              queryKey: ["landlord-profile"],
            });
          }
        }
      } catch (error) {
        console.error("Failed to sync profile after UAE Pass return:", error);
      }
    };

    handleUaePassReturn();
  }, [queryClient]);

  useEffect(() => {
    const initAuth = async () => {
      console.log("Initializing authentication...");
      try {
        await refreshAuthState();
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [refreshAuthState]);

  const login = useCallback(
    async (returnUrl: string, userType?: UserType, screen?: LoginScreen) => {
      await authService.login(returnUrl, userType, screen);
    },
    [],
  );

  const logout = useCallback(async () => {
    await unsubscribeFromPushNotifications();
    identifyAnalyticsUser(null);
    await authService.logout();
  }, []);

  const completeLogin = useCallback(async (): Promise<string> => {
    const user = await authService.completeLogin();
    recordSuccessfulLogin(user.profile.sub);
    setUser(user);
    setIsAuthenticated(true);

    const userType = user.profile?.UserType;
    try {
      if (userType === UserType.Agent || userType === UserType.AgencyManager) {
        await agentApi.syncProfileFromIdentity();
        await queryClient.invalidateQueries({ queryKey: ["agent-profile"] });
      } else if (userType === UserType.Tenant) {
        await renterApi.syncProfileFromIdentity();
        await queryClient.invalidateQueries({ queryKey: ["renter-profile"] });
      } else if (
        userType === UserType.Landlord ||
        userType === UserType.Manager
      ) {
        // Managers ride the landlord sync route — see comment above.
        await landlordApi.syncProfileFromIdentity();
        await queryClient.invalidateQueries({
          queryKey: ["landlord-profile"],
        });
      }
    } catch (error) {
      console.error("Failed to sync profile after login:", error);
    }

    return (user.state as UserState)?.from || "/";
  }, [queryClient]);

  const setUserType = useCallback(async (role: UserType) => {
    const refreshedUser = await authService.refreshUser();
    if (refreshedUser.profile.UserType !== role) {
      throw new Error(
        "The refreshed session did not confirm the selected workspace.",
      );
    }
    setUser(refreshedUser);
  }, []);

  if (isLoading) {
    return loadingElement;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated,
        logout,
        login,
        completeLogin,
        setUserType,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
